/**
 * Every claim the marketing site makes, in one file.
 *
 * `status` is not decoration. `live` means the capability is implemented and
 * wired into the running product today; `roadmap` means it is designed and
 * partially built but not yet shipping. The UI renders that distinction
 * visibly, so the site cannot drift into claiming things the product does not
 * do — a marketing page is a promise to a buyer, and an unshipped promise on a
 * pricing conversation is a refund conversation later.
 *
 * When a capability ships, flip its status here and nowhere else.
 */

export type Status = 'live' | 'roadmap';

export interface Capability {
  title: string;
  blurb: string;
  detail: string;
  status: Status;
}

export const PRODUCT = {
  name: 'Intent360',
  parent: 'Krill Technologies',
  tagline: 'Buying-intent intelligence for B2B demand gen',
  heroHeadline: 'Find out who actually read it.',
  heroSub:
    'A download tells you a file moved. Intent360 tells you someone spent eleven minutes on your pricing page, came back twice, and forwarded it to their VP of Engineering. That is the difference between a lead list and a buying signal.',
  heroNote: 'Measurement you can defend in a pipeline review.',
} as const;

/** Section 1 — the objection this product exists to answer. */
export const PAIN_POINTS = [
  {
    title: 'Better than download tracking',
    body:
      'A form fill records an email address and nothing else. It cannot tell an evaluator from someone who wanted the appendix. Intent360 measures the reading itself — active time, page depth, scroll completion, return visits — so the difference between skimming and studying shows up as a number.',
  },
  {
    title: 'A sales story that survives scrutiny',
    body:
      'Every score decomposes into the behaviour that produced it. When a rep asks why a lead is graded A, the answer is "covered 9 of 12 pages, 14 minutes active, returned twice, forwarded once" — not an opaque model output nobody can argue with.',
  },
  {
    title: 'Fits the stack you already run',
    body:
      'Content is distributed by your ESP or ours, read in the browser, and scores land in your CRM as ordinary contact properties. There is no new system for reps to learn and no data warehouse project.',
  },
] as const;

/** Section 2 — what the tracker actually measures. Numbers here are real. */
export const MEASURES = [
  {
    label: 'Pages viewed',
    detail:
      'Attributed by intersection ratio, not scroll position — the page occupying the most viewport gets the time. Zoom, spreads, and continuous scroll do not fool it.',
  },
  {
    label: 'Active read time',
    detail:
      'Time accrues only while the tab is visible, focused, and the reader has interacted within 30 seconds. A document left open over lunch earns nothing.',
  },
  {
    label: 'Scroll depth',
    detail:
      'Maximum depth reached per page, recorded continuously, so partial reads are distinguishable from full ones.',
  },
  {
    label: 'Repeat visits',
    detail:
      'Return sessions against the same asset — the strongest single indicator that a document is being evaluated rather than filed.',
  },
  {
    label: 'Share activity',
    detail:
      'Forwarding, printing, and downloading. Content leaving the original recipient usually means a buying committee is forming.',
  },
  {
    label: 'Engagement score',
    detail:
      'A 0–100 composite of the five above, weighted and normalised against the document’s own length, then explained in plain language.',
  },
] as const;

/** Section 3 — the four-step operational loop. */
export const FLOW = [
  {
    step: '01',
    title: 'Distribute content',
    body: 'Send gated assets through a campaign. Every recipient gets their own signed tracking link, minted at send time.',
  },
  {
    step: '02',
    title: 'Capture behaviour',
    body: 'The reading surface measures attention at the edge, filters bots and security scanners, and streams events to the scoring engine.',
  },
  {
    step: '03',
    title: 'Calculate engagement',
    body: 'Sessions score in seconds. Signals accumulate into an intent score that decays over time, so last quarter’s interest never reads as today’s.',
  },
  {
    step: '04',
    title: 'Push insights to CRM',
    body: 'Scores, grades, and the reasoning behind them land on the contact record your reps already work from.',
  },
] as const;

/** Section 4 — the intelligence chain, top to bottom. */
export const PIPELINE: Array<{ stage: string; note: string; status: Status }> = [
  { stage: 'Campaign', note: 'Tracked distribution, one signed link per recipient', status: 'live' },
  { stage: 'Content engagement', note: 'Active time, depth, scroll, repeat visits, shares', status: 'live' },
  { stage: 'Buying intent', note: 'Multi-source signals, time-decayed and capped', status: 'live' },
  { stage: 'Contact validation', note: 'Deliverability, role accounts, dormancy detection', status: 'roadmap' },
  { stage: 'Company intent', note: 'Account rollup and buying-committee breadth', status: 'roadmap' },
  { stage: 'AI lead score', note: 'Learned ranking with a transparent rules fallback', status: 'roadmap' },
  { stage: 'CRM', note: 'Scores and narrative written to the contact record', status: 'roadmap' },
];

/**
 * Section 5 — the intent engine's signal sources.
 *
 * The engine consumes all of these; the product currently *produces* the three
 * marked live. Weights and half-lives are the real configured values.
 */
export const SIGNALS: Array<{
  source: string;
  weight: number;
  halfLife: string;
  status: Status;
}> = [
  { source: 'PDF read', weight: 14, halfLife: '30 days', status: 'live' },
  { source: 'Pricing viewed', weight: 20, halfLife: '21 days', status: 'live' },
  { source: 'Case study read', weight: 12, halfLife: '30 days', status: 'live' },
  { source: 'Demo request', weight: 35, halfLife: '45 days', status: 'roadmap' },
  { source: 'Content share', weight: 18, halfLife: '30 days', status: 'roadmap' },
  { source: 'Webinar attendance', weight: 15, halfLife: '45 days', status: 'roadmap' },
  { source: 'Repeat website visit', weight: 6, halfLife: '14 days', status: 'roadmap' },
  { source: 'Email click', weight: 4, halfLife: '14 days', status: 'roadmap' },
  { source: 'Email open', weight: 1, halfLife: '7 days', status: 'roadmap' },
  { source: 'Search visit', weight: 8, halfLife: '14 days', status: 'roadmap' },
];

/** Section 6 — headline capabilities. */
export const CAPABILITIES: Capability[] = [
  {
    title: 'Campaign execution',
    blurb: 'Run campaigns from inside the product.',
    detail:
      'Compose, select recipients, send. Tracking links are minted per recipient at send time, so engagement attaches to a person and a campaign without an integration in between. One database, no reconciliation step.',
    status: 'live',
  },
  {
    title: 'Bot and scanner filtering',
    blurb: 'Corporate mail scanners do not count as readers.',
    detail:
      'Proofpoint, Mimecast, Barracuda, headless browsers, and datacenter traffic are identified and excluded before scoring. Without this, security appliances quietly manufacture your hottest leads.',
    status: 'live',
  },
  {
    title: 'Explainable scoring',
    blurb: 'Every number decomposes into behaviour.',
    detail:
      'Read time, depth, scroll completion, return behaviour, and actions each contribute a visible share of the composite. Reps get a sentence they can use on a call, not a black box.',
    status: 'live',
  },
  {
    title: 'Contact validation',
    blurb: 'Know when a contact has gone stale.',
    detail:
      'Deliverability checks, role-account and catch-all detection, and dormancy scoring identify contacts who have likely left the company — before a rep spends a week chasing them.',
    status: 'roadmap',
  },
  {
    title: 'Company intent',
    blurb: 'Accounts buy, not individuals.',
    detail:
      'Individual engagement rolls up to an account view with buying-committee breadth across job families and seniority, surfacing stages from Aware through Active Evaluation.',
    status: 'roadmap',
  },
  {
    title: 'AI intent prediction',
    blurb: 'Ranking that learns from closed deals.',
    detail:
      'A gradient-boosted model over company size, industry, job title, reading depth, frequency, repeat visits, email interaction, timing, and committee activity — with a transparent rules-based score until enough outcomes exist to train on.',
    status: 'roadmap',
  },
  {
    title: 'AI sales summary',
    blurb: 'A briefing, not a statistic.',
    detail:
      'Instead of "read 16 pages, 14 minutes", a short written summary with talking points and the main risk, generated from the behavioural record and ready to paste into a call prep note.',
    status: 'roadmap',
  },
  {
    title: 'CRM sync',
    blurb: 'Scores where reps already work.',
    detail:
      'Scores, grades, and reasoning written to contact properties, gated so only material changes sync. Closed-won and closed-lost outcomes flow back as training labels.',
    status: 'roadmap',
  },
];

export const FAQ = [
  {
    q: 'How is this different from marketing automation reporting?',
    a: 'Automation platforms record that an email was opened and a file was downloaded. Neither event says anything about whether the content was read. Intent360 measures the reading session itself and scores it against the document’s own length, so a 30-second skim of a 20-minute report scores as a skim.',
  },
  {
    q: 'Does it work without cookies or third-party tracking?',
    a: 'Yes. Measurement is first-party and tied to a signed link you issued to a known recipient. There is no third-party cookie, no cross-site tracking, and no identity resolution against people who never asked to hear from you.',
  },
  {
    q: 'What stops a security scanner from inflating scores?',
    a: 'A dedicated filter runs before scoring: known scanner user agents, headless browser signatures, automation flags, impossible reading speeds, zero-interaction sessions, and datacenter network origins are all excluded and recorded as such.',
  },
  {
    q: 'Can we run it against our existing ESP?',
    a: 'Campaigns can be sent from Intent360 directly, or you can distribute tracked links through the ESP you already use. The measurement does not care which system delivered the mail.',
  },
];
